import { fleetApi } from "@App/apis/graphql/fleet/fleetApi";
import type { FleetErrorCode } from "@App/apis/graphql/fleet/errors";
import { setFleetVehicleReturn } from "@App/state/reducers/fleet";
import { useState } from "react";
import { useDispatch } from "react-redux";

type VehicleReturnContractError =
  | "not_found"
  | "does_not_match"
  | "wrong_contract_type"
  | "general"
  | "wrong_format"
  | "cannot_book"
  | null;

interface UseVehicleReturnContractProps {
  organizationId: number;
  contractNumber: string | null;
  numberPlate: string | null;
  onSubmit: () => void;
  contractNumberRegExp?: RegExp;
  canBook?: (contractEndDate: Date) => boolean;
}

function mapFleetError(code: FleetErrorCode): VehicleReturnContractError {
  switch (code) {
    case "contract_not_found":
      return "not_found";
    case "does_not_match":
      return "does_not_match";
    case "wrong_contract_type":
      return "wrong_contract_type";
    case "general_error":
      return "general";
  }
}

export function useVehicleReturnContract({
  organizationId,
  contractNumber,
  numberPlate,
  onSubmit,
  contractNumberRegExp,
  canBook = () => true,
}: UseVehicleReturnContractProps) {
  const [error, setError] = useState<VehicleReturnContractError>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const isDataComplete = !!(organizationId && contractNumber && numberPlate);

  const dispatch = useDispatch();

  async function onClick() {
    if (!isDataComplete) return;
    if (contractNumberRegExp && !contractNumberRegExp.test(contractNumber)) {
      setError("wrong_format");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fleetApi.vehicleReturn.getVehicleReturnContract({
        organizationId: String(organizationId),
        agreementNumber: contractNumber,
        numberPlate,
      });
      if (result.ok === false) {
        setError(mapFleetError(result.error));
        return;
      }
      if (canBook(result.contract.endDate) === false) {
        setError("cannot_book");
        return;
      }
      dispatch(
        setFleetVehicleReturn({
          agreementNumber: result.contract.agreementNumber,
          endDate: result.contract.endDate.toISOString(),
          authorizedReturnLocationIds: result.contract.authorizedReturnLocationIds,
        }),
      );
      onSubmit();
    } catch {
      setError("general");
    } finally {
      setLoading(false);
    }
  }

  return {
    error,
    loading,
    isDataComplete,
    onClick,
  };
}
